import {
  DataGridPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridFilterItem,
  GridPinnedColumnFields,
  GridRowParams,
  GridRowSelectionModel,
  GridSingleSelectColDef,
  GridValidRowModel,
  useGridApiRef
} from '@mui/x-data-grid-pro';

import { ComponentSpinner } from '../Loading/ComponentSpinner';
import {
  GridPreferences,
  PrefKey,
  UserPreferences,
  getCurrentPreferences,
  getUserPreferencesSubject,
  updateTableState
} from '../../../store/User';
import { CustomToolbar } from './CustomToolbar';
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { CustomFilterPanel } from './CustomFilterPanel';
import { Icon } from '../../Atoms/Icon';
import { ApiAdvancedFilter, updateGridRef } from '../../../store/GeneralStore';
import { CustomColumnsPanel } from './CustomColumnsPanel';
import { applyDefaultHiddenColumns } from './columnVisibility';
import { useVisibilitySwitches } from './useVisibilitySwitches';
import { NO_OVERRIDDEN_COLUMNS } from './accountDefaultLayout';
import { completeColumnOrder, reconcileMissingColumnOrder } from './columnOrder';
import { partitionFilterModelItems } from './filterModelSanitizer';
import { ExpandableCellProvider, useExpandableCells } from './CellRenderers/ExpandableTextCell';
import {
  ColumnDimensionMap,
  readSavedColumnDimensions,
  recordColumnWidth,
  unsavedDimensions,
  withColumnDimensions
} from './columnDimensions';
import { prepareGridColumns } from './prepareGridColumns';
import { DD_GRID_CELL_SLOT_PROPS } from '../../../utils/telemetry';

// A server-filtered grid pays a round trip per keystroke, so hold the request until typing settles.
const SERVER_SEARCH_DEBOUNCE_MS = 500;

export interface UpstackDataGridProps {
  title: string;
  columns: GridSingleSelectColDef[] | GridColDef[] | undefined;
  rows: GridValidRowModel[];
  loadingData: boolean;
  page: PrefKey;
  gridSettings: GridPreferences;
  /** The user's own saved values for the columns an account's later change decides here, saved back unless switched. */
  overriddenColumns?: Readonly<Record<string, boolean>>;
  showSearch: boolean;
  pinnedColumns?: GridPinnedColumnFields;
  dataCy?: string;
  triggerSave?: boolean;
  handleNewTabModalOpen?: () => void;
  columnGroupSortOrder?: string[];
  rowPrimaryKeys?: string[];
  apiFilter?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchFunction?: (filters: any) => void;
  rowCount?: number;
  filterKeys?: string[];
  enableRowSelection?: boolean;
  isRowSelectable?: (params: GridRowParams) => boolean;
  onRowClick?: (params: GridRowParams) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowSelectionModelChange?: (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void;
  defaultVisibleColumns?: string[];
  containerClassName?: string;
  processRowUpdate?: (
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel
  ) => GridValidRowModel | Promise<GridValidRowModel>;
  onProcessRowUpdateError?: (error: unknown) => void;
  // Per-cell editability; the column's own `editable` is one boolean for every row, so a row-dependent gate lives here.
  isCellEditable?: (params: GridCellParams) => boolean;
}

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    title: string;
    showSearch: boolean;
    setFilterButtonEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
    saveState: () => void;
    searchDebounceMs?: number;
  }
  interface ColumnsPanelPropsOverrides {
    columnGroupSortOrder?: string[];
  }
}

export const DEFAULT_TABLE_STYLES = {
  sx: {
    overflowX: 'scroll',
    overflowY: 'hidden',
    border: 'unset',
    '.MuiDataGrid-cell': {
      fontFamily: 'sans-serif',
      borderLeft: '.5px solid lightgrey',
      '&:first-child': {
        borderLeft: 'unset'
      },
      '&:last-child': {
        borderRight: 'unset'
      }
    },
    '.MuiDataGrid-overlayWrapper': {
      minHeight: '0'
    },
    '.MuiDataGrid-columnHeader': {
      fontFamily: 'sans-serif',
      fontSize: '.75rem',
      color: '#5D5D5D',
      borderLeft: '.5px solid lightgrey',
      borderTop: '.5px solid lightgrey',
      '&:first-child': {
        borderLeft: 'unset !important'
      },
      '&:last-child': {
        borderRight: 'unset'
      }
    }
  },
  headerHeight: 40,
  rowHeight: 40
};

const columnsIcon = () => {
  return <Icon type="columnsIcon" />;
};

const exportIcon = () => {
  return <Icon type="exportIcon" />;
};

const filterIcon = () => {
  return <Icon type="filterIcon" />;
};

export const UpstackDataGrid = ({
  title,
  rows,
  columns,
  loadingData,
  page,
  gridSettings = {} as GridPreferences,
  overriddenColumns = NO_OVERRIDDEN_COLUMNS,
  showSearch = false,
  pinnedColumns,
  dataCy = '',
  triggerSave = false,
  handleNewTabModalOpen,
  columnGroupSortOrder,
  rowPrimaryKeys = [],
  apiFilter = false,
  fetchFunction,
  rowCount = apiFilter ? 0 : undefined,
  filterKeys = [],
  enableRowSelection = false,
  isRowSelectable,
  onRowClick,
  onRowSelectionModelChange = () => {},
  defaultVisibleColumns,
  containerClassName = 'h-[45rem]',
  processRowUpdate,
  onProcessRowUpdateError,
  isCellEditable
}: UpstackDataGridProps) => {
  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialStatePro | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>();
  const [filterableColumns, setFilterableColumns] = useState<GridSingleSelectColDef[] | GridColDef[]>();
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
  const [stateDirty, setStateDirty] = useState(false);
  // The grid hiding a late column is not a change the user has to save.
  const hidingLateColumnsRef = useRef(false);
  const visibilitySwitches = useVisibilitySwitches();
  // The catalog's fields as of the last lay-out or pass, so only a column new since then counts as late.
  const catalogFieldsRef = useRef<Set<string> | null>(null);
  // True only after the user drags a column this mount. Distinguishes a real live reorder (keep it) from
  // MUI resetting orderedFields to catalog order when the async columns prop rebuilds (ignore that).
  const userReorderedRef = useRef(false);
  // Widths the grid must keep re-applying: MUI overwrites a resized column with the def's own width every time
  // the columns prop rebuilds, and it does not consult the saved state on that path.
  const savedDimensionsRef = useRef<ColumnDimensionMap>({});
  // Held apart from the saved widths because ANY grid's save re-emits preferences to every mounted grid, so
  // this one is handed its own pre-drag widths back a round trip later; folding the two would undo the drag.
  const draggedDimensionsRef = useRef<ColumnDimensionMap>({});
  // Which of those drags are still only on screen. Separate from the drags themselves, since a grid handed an
  // inline gridSettings literal is never told its own save landed and would warn about it for ever.
  const unsavedDragsRef = useRef<ColumnDimensionMap>({});
  const columnDimensions = () => ({ ...savedDimensionsRef.current, ...draggedDimensionsRef.current });
  const [tableLoading, setTableLoading] = useState(false);
  const [initialStateRestored, setInitialStateRestored] = useState(false);
  const [restoredCount, setRestoredCount] = useState<number>(0);
  const sideMode = apiFilter ? 'server' : 'client';
  // What the grid actually renders: a column leaves it by being hidden OR by dropping out of the column set.
  const visibleFields = useMemo(
    () => filterableColumns?.filter((column) => !hiddenColumns?.includes(column.field)).map((column) => column.field),
    [filterableColumns, hiddenColumns]
  );
  const {
    context: expandableCellContext,
    getRowHeight,
    onCellClick,
    onCellKeyDown,
    clearExpanded
  } = useExpandableCells(visibleFields, DEFAULT_TABLE_STYLES.rowHeight);
  // Saved filter items whose column had not loaded when the view was restored.
  const deferredFilterItems = useRef<GridFilterItem[]>([]);
  const pendingFiltersRef = useRef<ApiAdvancedFilter | null>(null);
  const lastRequestRef = useRef<string>('');
  const lastSearchTermRef = useRef<string>('');
  const fetchFunctionRef = useRef(fetchFunction);
  fetchFunctionRef.current = fetchFunction;

  // Debounce the request, not the box, so a rapid type-then-clear cannot resurrect the abandoned term.
  const runFetch = useMemo(
    () =>
      debounce(async () => {
        const filters = pendingFiltersRef.current;
        if (!filters || !fetchFunctionRef.current) return;
        // A response can resettle the grid into the query it just answered; don't ask twice.
        const request = JSON.stringify(filters);
        if (request === lastRequestRef.current) return;
        lastRequestRef.current = request;
        setTableLoading(true);
        try {
          await fetchFunctionRef.current(filters);
        } catch {
          // A rejection must leave the same query askable again, or the dedup above locks it out for good.
          if (lastRequestRef.current === request) lastRequestRef.current = '';
        } finally {
          // An older fetch finishing later must not clear the spinner a newer one is still waiting on.
          if (lastRequestRef.current === request || !lastRequestRef.current) setTableLoading(false);
        }
      }, SERVER_SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => runFetch.cancel(), [runFetch]);

  // Watched in the store, so the guard below holds whatever settings the page hands the grid.
  const lastStoredPageRef = useRef<PrefKey | null>(null);
  useEffect(() => {
    const sub = getUserPreferencesSubject().subscribe((preferences) => {
      if (page in (preferences?.content ?? {})) lastStoredPageRef.current = page;
    });
    return () => sub.unsubscribe();
  }, [page]);

  // Keyed on page/gridSettings too: the Projects page flips a mounted grid from one mode's key to the
  // other's without remounting, so a stale closure would write the layout under the previous key.
  const saveState = useCallback(async () => {
    if (apiRef?.current?.exportState) {
      const currentState = apiRef.current.exportState();
      const currentSettings: UserPreferences = JSON.parse(
        JSON.stringify(getCurrentPreferences() || ({} as UserPreferences))
      );
      if (!currentSettings?.content) return;

      // A saved view is a snapshot of what it shows, defaults included, and the API stamps none of it.
      const isView = gridSettings.customView === true;
      const sent = visibilitySwitches.send(
        currentState.columns?.columnVisibilityModel ?? {},
        overriddenColumns,
        isView
      );
      const settingsCopy = JSON.parse(JSON.stringify(gridSettings));
      delete settingsCopy.visibilitySwitched;
      (currentSettings.content[page] as GridPreferences) = {
        ...settingsCopy,
        muiConfig: {
          ...currentState,
          columns: { ...currentState.columns, columnVisibilityModel: sent.columnVisibilityModel }
        },
        ...(sent.switched.length && !isView ? { visibilitySwitched: sent.switched } : {})
      };
      const saved = await updateTableState(currentSettings, page);
      // The state just written carries the drags, whether or not this grid is ever handed it back.
      if (!saved?.error) {
        unsavedDragsRef.current = {};
        visibilitySwitches.landed(sent);
      }
      setStateDirty(false);
    }
  }, [apiRef, page, gridSettings, overriddenColumns, visibilitySwitches]);

  // The flush effect below must call the latest saveState, through the closing check, without re-running on its
  // identity: a gridSettings-keyed callback wired straight into a [saveState] effect turns every preferences
  // emission into a cleanup PUT, whose response re-emits and loops.
  const closingSaveRef = useRef<() => void>(saveState);
  closingSaveRef.current = () => {
    // A view deleted or renamed while its grid was open would otherwise come back with the grid's closing save.
    if (lastStoredPageRef.current === page && !(page in (getCurrentPreferences()?.content ?? {}))) return;
    saveState();
  };

  useEffect(() => {
    updateGridRef(page, apiRef);
    // A drag belongs to the view it was made in, and the page key is the only thing that tells a real
    // view change from the same view's preferences being re-emitted.
    draggedDimensionsRef.current = {};
    unsavedDragsRef.current = {};
  }, [apiRef, page]);

  useEffect(() => {
    if (triggerSave) {
      // Open the naming modal only once the layout PUT has landed, or NewViewModal seeds the new view
      // from a pre-save snapshot (dropping filters) or from a key that hasn't been written yet. The catch
      // resets the trigger even if saveState throws synchronously (a stuck flag would leave the disabled
      // Save As View button dead) and swallows the otherwise-unhandled rejection.
      saveState()
        .catch(() => undefined)
        .then(() => {
          if (handleNewTabModalOpen) handleNewTabModalOpen();
        });
    }
  }, [triggerSave]);

  useEffect(() => {
    const confirmNavigation = (event: { preventDefault: () => void; returnValue: string }) => {
      if (stateDirty) {
        event.preventDefault();
        event.returnValue = 'You have unsaved table changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', confirmNavigation);
    return () => {
      window.removeEventListener('beforeunload', confirmNavigation);
    };
  }, [stateDirty]);

  useLayoutEffect(() => {
    const flush = () => closingSaveRef.current();
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, []);

  // Each mount replaces apiRef.current and the view below is derived again on that, so a render must follow the mount.
  const gridShown = !loadingData && !!initialState && !!filterableColumns;
  const [, renderAfterMount] = useReducer((renders: number) => renders + 1, 0);
  useLayoutEffect(() => {
    if (gridShown) renderAfterMount();
  }, [gridShown]);

  useEffect(() => {
    if (apiRef.current) {
      const state: GridInitialStatePro = JSON.parse(
        JSON.stringify(gridSettings.muiConfig || ({} as GridInitialStatePro))
      );

      if (pinnedColumns) {
        state.pinnedColumns = pinnedColumns;
        const { columns = {} } = state;
        const { left = [], right = [] } = pinnedColumns;
        const orderedFields = [
          ...left,
          ...(state?.columns?.orderedFields || []).filter((field) => !left.includes(field) && !right.includes(field)),
          ...right
        ];
        columns.orderedFields = orderedFields;
        state.columns = columns;
      }

      // Handle default visible columns if no saved column visibility exists
      if (defaultVisibleColumns && !state?.columns?.columnVisibilityModel && columns) {
        if (!state.columns) state.columns = {};

        // Create a visibility model where all columns are hidden by default
        const columnVisibilityModel: Record<string, boolean> = {};
        columns.forEach((column) => {
          columnVisibilityModel[column.field] = defaultVisibleColumns.includes(column.field);
        });

        state.columns.columnVisibilityModel = columnVisibilityModel;
      }

      // Force defaultHidden columns off when the visibility model omits them — a saved layout that
      // predates them, or a tab with none saved yet — so optional columns don't leak on.
      let filled: string[] = [];
      if (columns) {
        state.columns ??= {};
        state.columns.columnVisibilityModel ??= {};
        filled = applyDefaultHiddenColumns(state.columns.columnVisibilityModel, columns);

        const savedOrder = state.columns.orderedFields;
        if (savedOrder?.length) reconcileMissingColumnOrder(savedOrder, columns);
      }

      if (state.pagination && sideMode === 'client') state.pagination.rowCount = undefined;

      // Strip saved filter items that would crash GridFilterForm ("Cannot read properties
      // of undefined (reading 'filterable')") — see partitionFilterModelItems. An item whose column has
      // merely not loaded yet is set aside instead, for the effect below to put back when it does.
      const filterModel = state.filter?.filterModel;
      if (filterModel?.items) {
        const { items, awaitingColumn } = partitionFilterModelItems(filterModel.items, columns);
        filterModel.items = items;
        deferredFilterItems.current = awaitingColumn;
      } else {
        deferredFilterItems.current = [];
      }

      visibilitySwitches.layOut(state.columns?.columnVisibilityModel ?? {}, filled);
      // Columns that arrive with the settings are laid out here, or the pass below would read the previous screen.
      if (columns) catalogFieldsRef.current = new Set(columns.map((col) => col.field));
      setInitialState(state);

      const hiddenColumns = state?.columns?.columnVisibilityModel
        ? Object.keys(state?.columns?.columnVisibilityModel).filter(
            (field) => !(state?.columns?.columnVisibilityModel || {})[field]
          )
        : [];
      setHiddenColumns(hiddenColumns);
    }
  }, [gridSettings, apiRef.current, defaultVisibleColumns]);

  useEffect(() => {
    if (initialState && apiRef?.current?.restoreState) {
      savedDimensionsRef.current = readSavedColumnDimensions(initialState);
      // This state can be any other grid's save re-emitted, which knows nothing of a drag made here: what it
      // has not caught up with is still unsaved, and has to survive both the restore and the dirty reset.
      unsavedDragsRef.current = unsavedDimensions(unsavedDragsRef.current, savedDimensionsRef.current);
      apiRef.current.restoreState(withColumnDimensions(initialState, columnDimensions()));
      // A restored term is a baseline, not typing, so the first load is not held back by the debounce.
      lastSearchTermRef.current = (initialState.filter?.filterModel?.quickFilterValues || []).join(' ');
      setStateDirty(Object.keys(unsavedDragsRef.current).length > 0);
      // The just-restored order is authoritative again; any prior live drag is now saved/reflected here.
      userReorderedRef.current = false;
      setInitialStateRestored(true);
    }
  }, [initialState]);

  useEffect(() => {
    if (initialStateRestored) handleTableChange();
  }, [initialStateRestored]);

  // Columns can arrive after the view is restored — custom fields come from their own fetch, and the catalog
  // itself is filtered by account config that lands late. Adding back only the items whose column the grid now
  // renders keeps the saved filter, where re-deriving the whole state would replace a sort or search made
  // meanwhile. Keyed on the rendered set, not the prop: MUI drops an item its column lookup cannot place.
  useEffect(() => {
    if (!initialStateRestored || !filterableColumns?.length || !deferredFilterItems.current.length) return;
    // Clearing what is held back before knowing it can be applied would drop it with nowhere left to read it.
    if (!apiRef.current?.setFilterModel) return;

    const { items: restorable, awaitingColumn } = partitionFilterModelItems(
      deferredFilterItems.current,
      filterableColumns
    );
    deferredFilterItems.current = awaitingColumn;
    if (!restorable.length) return;

    const filterModel = apiRef.current.exportState().filter?.filterModel;
    const current = filterModel?.items || [];
    // Only a real id names an item: a view saved before MUI stamped them carries none, and one absent id
    // matched against another would read two different filters as the same one.
    const alreadyShown = (item: GridFilterItem) =>
      item.id !== undefined && current.some((shown) => shown.id === item.id);

    apiRef.current.setFilterModel({
      ...filterModel,
      items: [...current, ...restorable.filter((item) => !alreadyShown(item))]
    });
  }, [filterableColumns, initialStateRestored]);

  // The restore does not re-run when only columns change, so a defaultHidden column that joins the catalog later would
  // show. Only new fields count: a model MUI's own panel saved holds no key for a column shown by Show All.
  // New means new since the view was laid out, as columns can grow between computing the view and restoring it.
  useEffect(() => {
    if (!initialStateRestored || !columns || !apiRef.current?.setColumnVisibilityModel) return;

    const previousFields = catalogFieldsRef.current;
    catalogFieldsRef.current = new Set(columns.map((col) => col.field));
    const newColumns = previousFields ? columns.filter((col) => !previousFields.has(col.field)) : columns;
    if (!newColumns.length) return;

    const columnVisibilityModel = { ...apiRef.current.exportState().columns?.columnVisibilityModel };
    const filled = applyDefaultHiddenColumns(columnVisibilityModel, newColumns);
    if (!filled.length) return;
    visibilitySwitches.fillLate(filled);

    hidingLateColumnsRef.current = true;
    // MUI fires the change event inside this call, so its handler still sees the flag; a deferred event would read it reset.
    apiRef.current.setColumnVisibilityModel(columnVisibilityModel);
    hidingLateColumnsRef.current = false;
  }, [columns, initialStateRestored]);

  useEffect(() => {
    if (!columns || !hiddenColumns) return setFilterableColumns([]);

    // Prefer the grid's live order ONLY when the user actually dragged a column this mount — that keeps an
    // un-saved drag through a data-driven columns rebuild (e.g. a cell edit). Otherwise use the saved order:
    // on remount the live order is a catalog-reset artifact (MUI clears orderedFields when the columns prop
    // rebuilds), so trusting it would lock in the wrong order despite correct saved prefs.
    const liveOrdered = userReorderedRef.current ? apiRef.current?.exportState?.().columns?.orderedFields : undefined;
    const orderedFields = liveOrdered?.length ? liveOrdered : initialState?.columns?.orderedFields;

    setFilterableColumns(
      prepareGridColumns({
        columns,
        hiddenColumns,
        // A column that arrived after the restore, such as a custom field, takes its place instead of going last.
        orderedFields: orderedFields?.length ? completeColumnOrder(orderedFields, columns) : orderedFields,
        dimensions: columnDimensions()
      })
    );
  }, [columns, hiddenColumns?.length]);

  function getRowId(row: GridValidRowModel) {
    return rowPrimaryKeys.length === 0
      ? row['id']
      : rowPrimaryKeys
          .map((key) => row[key])
          .filter((value) => value != null)
          .map((k) => String(k))
          .join('|');
  }

  useEffect(() => {
    if (restoredCount === 2 && initialState)
      apiRef.current.restoreState(withColumnDimensions(initialState, columnDimensions()));
  }, [restoredCount, initialState, apiRef]);

  const handleTableChange = async () => {
    setStateDirty(true);
    // A filter, sort or page change can take the open row out of the view; leaving it open would
    // re-open it, at its old height, on a row the reader never opened.
    clearExpanded();

    if (apiFilter && fetchFunction && initialStateRestored) {
      const currentState = apiRef?.current?.exportState();
      if (restoredCount < 2) {
        if (restoredCount === 1) {
          setRestoredCount(2);
          return;
        }
        setRestoredCount(1);
      }

      let filters: ApiAdvancedFilter = (currentState?.filter?.filterModel?.items || []).reduce<ApiAdvancedFilter>(
        (filterSet, item) => {
          if (
            (['isEmpty', 'isNotEmpty'].includes(item.operator) ||
              (item.value !== undefined && item.value !== null && item.value !== '')) &&
            ((item.operator === 'isAnyOf' && Array.isArray(item.value) && item.value.length > 0) ||
              (item.operator !== 'isAnyOf' && !Array.isArray(item.value)))
          ) {
            filterSet.filters?.push({
              field: item.field,
              operator: item.operator,
              value: item.value
            });
          }

          return filterSet;
        },
        { filters: [] } as ApiAdvancedFilter
      );
      filters.logicalOperator = currentState?.filter?.filterModel?.logicOperator;

      if (currentState?.filter?.filterModel?.quickFilterValues?.length && showSearch) {
        const value = currentState.filter.filterModel.quickFilterValues.join(' ');
        filters = (filterKeys || []).reduce<ApiAdvancedFilter>(
          (filterSet, item) => {
            filterSet.filters?.push({
              field: item,
              operator: 'is',
              value: value
            });

            return filterSet;
          },
          { filters: [] } as ApiAdvancedFilter
        );
        filters.logicalOperator = 'or';
      }
      filters.page = (currentState?.pagination?.paginationModel?.page || 0) + 1;
      filters.pageSize = currentState?.pagination?.paginationModel?.pageSize || 50;
      filters.sort = currentState?.sorting?.sortModel?.[0]?.field || '';
      filters.direction = currentState?.sorting?.sortModel?.[0]?.sort || '';

      pendingFiltersRef.current = filters;
      const searchTerm = (currentState?.filter?.filterModel?.quickFilterValues || []).join(' ');
      const searchChanged = searchTerm !== lastSearchTermRef.current;
      lastSearchTermRef.current = searchTerm;

      runFetch();
      // Only typing waits; every other change answers now, carrying whatever the box holds by then.
      if (!searchChanged || !searchTerm) runFetch.flush();
    }
  };

  return (
    <>
      {(loadingData || !initialState) && (
        <div className={`${containerClassName} p-32`}>
          <ComponentSpinner />
        </div>
      )}
      {!loadingData && initialState && filterableColumns && (
        <div className={containerClassName}>
          <ExpandableCellProvider value={expandableCellContext}>
            <DataGridPro
              rows={rows}
              data-cy={dataCy}
              data-testid="data-grid"
              columns={filterableColumns}
              apiRef={apiRef}
              columnHeaderHeight={DEFAULT_TABLE_STYLES.headerHeight}
              rowHeight={DEFAULT_TABLE_STYLES.rowHeight}
              getRowHeight={getRowHeight}
              onCellClick={onCellClick}
              onCellKeyDown={onCellKeyDown}
              sx={DEFAULT_TABLE_STYLES.sx}
              pagination
              loading={tableLoading}
              paginationMode={sideMode}
              sortingMode={sideMode}
              filterMode={sideMode}
              disableRowSelectionOnClick
              checkboxSelection={enableRowSelection}
              isRowSelectable={isRowSelectable}
              onRowClick={onRowClick}
              onRowSelectionModelChange={onRowSelectionModelChange}
              initialState={initialState}
              slots={{
                toolbar: CustomToolbar,
                filterPanel: CustomFilterPanel,
                // MUI's own panel shows a column by deleting its key, which a reload reads back as the column's default.
                columnsPanel: CustomColumnsPanel,
                columnMenuIcon: columnsIcon,
                exportIcon: exportIcon,
                openFilterButtonIcon: filterIcon
              }}
              slotProps={{
                cell: DD_GRID_CELL_SLOT_PROPS,
                panel: {
                  anchorEl: filterButtonEl
                },
                columnsPanel: {
                  columnGroupSortOrder
                },
                toolbar: {
                  showSearch,
                  title,
                  setFilterButtonEl,
                  saveState,
                  // MUI's own pause survives the clear button and would put the abandoned term back.
                  searchDebounceMs: apiFilter ? 0 : undefined
                }
              }}
              rowCount={rowCount}
              onFilterModelChange={handleTableChange}
              onPaginationModelChange={handleTableChange}
              onSortModelChange={handleTableChange}
              onColumnOrderChange={() => {
                userReorderedRef.current = true;
                setStateDirty(true);
              }}
              onColumnWidthChange={({ colDef, width }) => {
                draggedDimensionsRef.current = recordColumnWidth(draggedDimensionsRef.current, colDef.field, width);
                unsavedDragsRef.current = recordColumnWidth(unsavedDragsRef.current, colDef.field, width);
                setStateDirty(true);
              }}
              onColumnVisibilityModelChange={(columnVisibility) => {
                const updatedHiddenColumns = Object.keys(columnVisibility).filter((field) => !columnVisibility[field]);
                setHiddenColumns(updatedHiddenColumns);
                if (!hidingLateColumnsRef.current) setStateDirty(true);
              }}
              pageSizeOptions={[50, 100, 200]}
              getRowId={getRowId}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={onProcessRowUpdateError}
              isCellEditable={isCellEditable}
            />
          </ExpandableCellProvider>
        </div>
      )}
    </>
  );
};
